import { format, formatDistance, fromUnixTime, isValid, parse } from 'date-fns';
import { formatMobile } from '@/filters/helpers';
import {
  displayFormat,
  isSensitive,
  REDACTED_DISPLAY_LABEL,
} from '@/lib/fieldFormats';

/**
 * Parse a timestamp value (seconds, milliseconds, or ISO string) into a Date.
 * Returns null if the value cannot be parsed into a valid date.
 */
export const parseFieldDate = (value) => {
  if (!value) return null;

  if (typeof value === 'number') {
    // Distinguish seconds vs milliseconds: values above ~10 billion are ms
    const seconds = value > 9999999999 ? Math.floor(value / 1000) : value;
    const date = fromUnixTime(seconds);
    return isValid(date) ? date : null;
  }

  if (typeof value === 'string') {
    const num = Number(value);
    if (!isNaN(num) && num > 946684800) {
      const seconds = num > 9999999999 ? Math.floor(num / 1000) : num;
      const date = fromUnixTime(seconds);
      return isValid(date) ? date : null;
    }
    // Date-only strings (YYYY-MM-DD) must be parsed as local time, not UTC,
    // otherwise they display as the previous day in negative-offset timezones.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const date = parse(value, 'yyyy-MM-dd', new Date());
      return isValid(date) ? date : null;
    }
    const date = new Date(value);
    return isValid(date) ? date : null;
  }

  return null;
};

/**
 * Format a date/time field value for full display (e.g. contact edit, table).
 * Returns a string like "Jan 15 2025, 03:30 PM CST" or a relative time for last_seen.
 */
export const formatDateFull = (value, fieldName) => {
  const date = parseFieldDate(value);
  if (!date) return '';

  if (fieldName === 'last_seen') {
    return formatDistance(date, new Date(), { addSuffix: true });
  }

  const formatted = format(date, 'MMM dd yyyy, hh:mm a');
  const tz = date
    .toLocaleTimeString('en-us', { timeZoneName: 'short' })
    .split(' ')[2];

  return `${formatted} ${tz}`;
};

/**
 * Format a date/time field value for compact display (e.g. board cards).
 * Returns a shorter string like "Jan 15, 2025".
 */
export const formatDateCompact = (value) => {
  const date = parseFieldDate(value);
  if (!date) return '';

  return format(date, 'MMM d, yyyy');
};

/**
 * Format a field value for display as a plain-text string.
 *
 * @param {*}      value     - The raw field value
 * @param {string} type      - The field type (e.g. 'time', 'date', 'bool', 'secret', 'list', etc.)
 * @param {object} [options] - Extra context
 * @param {string} [options.fieldName] - The field name (e.g. 'mobile', 'last_seen')
 * @param {string} [options.mode]      - 'compact' for board cards, 'full' for tables/edit views
 * @returns {string}
 */
export const formatFieldValue = (value, type, options = {}) => {
  const {
    fieldName = '',
    mode = 'compact',
    format: fieldFormat = null,
    context = mode === 'compact' ? 'list' : 'detail',
  } = options;

  // Nullish / empty
  if (value == null || value === '') return '';

  // Secret fields must never render their decrypted value here, regardless
  // of format — formats like bank account/routing numbers aren't marked
  // sensitive but still shouldn't leak plaintext for an encrypted field.
  if (type === 'secret') {
    return fieldFormat && isSensitive(fieldFormat)
      ? REDACTED_DISPLAY_LABEL
      : '••••••••';
  }

  if (fieldFormat) {
    return displayFormat(fieldFormat, value, { context });
  }

  // --- Type-aware formatting ---

  if (type === 'bool' || typeof value === 'boolean') {
    if (value === true || value === 'true' || value === 1) return 'Yes';
    if (value === false || value === 'false' || value === 0) return 'No';
    return String(value);
  }

  if (type === 'date') {
    return formatDateCompact(value);
  }

  if (type === 'time') {
    if (mode === 'compact') {
      return formatDateCompact(value);
    }
    return formatDateFull(value, fieldName);
  }

  // Arrays (list, related_contacts, related_custom_object, tags)
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Mobile phone formatting (by field name, not type)
  if (fieldName === 'mobile' && value) {
    return formatMobile(value) || String(value);
  }

  // Points / numeric display
  if (type === 'int' && typeof value === 'number') {
    return value.toLocaleString('en-US');
  }

  return String(value);
};
